import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { db } from "../supabase";
import { useAngemeldet, useSitzung } from "../sitzung";
import { FOTO_BUCKET, zuschneiden } from "../foto";
import { fehlertext } from "../fehler";
import { Knopf } from "./Knopf";
import { useHinweis } from "./Hinweise";

/** Eigenes Profilfoto hochladen und entfernen: im Profil, in „Willkommen“ und in der Karte „Erste Schritte“. */
export function useFoto() {
  const sitzung = useAngemeldet();
  const { neuLaden } = useSitzung();
  const { zeigen } = useHinweis();
  const qc = useQueryClient();
  const [laeuft, setLaeuft] = useState(false);
  const pfad = `${sitzung.person.id}.jpg`;

  async function hochladen(f: File | undefined) {
    if (!f) return;
    if (!/^image\/(jpeg|png|webp|heic|heif)$/.test(f.type)) {
      zeigen("Bitte ein Foto als JPG oder PNG wählen.", { fehler: true });
      return;
    }
    setLaeuft(true);
    try {
      const bild = await zuschneiden(f);
      const hoch = await db().storage.from(FOTO_BUCKET).upload(pfad, bild, { upsert: true, contentType: "image/jpeg", cacheControl: "3600" });
      if (hoch.error) throw hoch.error;
      const p = await db().from("app_personen").update({ foto_pfad: pfad }).eq("id", sitzung.person.id);
      if (p.error) throw p.error;
      await qc.invalidateQueries({ queryKey: ["foto", pfad] });
      await neuLaden();
      zeigen("Foto gespeichert.");
    } catch (err) {
      zeigen(fehlertext(err), { fehler: true });
    } finally {
      setLaeuft(false);
    }
  }

  async function entfernen() {
    setLaeuft(true);
    const p = await db().from("app_personen").update({ foto_pfad: null }).eq("id", sitzung.person.id);
    const r = p.error ? null : await db().storage.from(FOTO_BUCKET).remove([pfad]);
    setLaeuft(false);
    const f = p.error ?? r?.error;
    if (f) {
      zeigen(fehlertext(f), { fehler: true });
      return;
    }
    await neuLaden();
    zeigen("Foto entfernt. Die App zeigt jetzt deine Initialen.");
  }

  return { laeuft, hatFoto: !!sitzung.person.foto_pfad, hochladen, entfernen };
}

/** Knopf „Foto hochladen“ mit verstecktem Dateifeld. Am Handy bietet das Feld Kamera und Fotos an. */
export function FotoKnopf({ foto, art = "normal", klein = false }: { foto: ReturnType<typeof useFoto>; art?: "primaer" | "normal"; klein?: boolean }) {
  const datei = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={datei}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        hidden
        onChange={(e) => {
          void foto.hochladen(e.target.files?.[0]).finally(() => {
            if (datei.current) datei.current.value = "";
          });
        }}
      />
      <Knopf art={art} klein={klein} onClick={() => datei.current?.click()} disabled={foto.laeuft}>
        {foto.laeuft ? "Einen Moment …" : foto.hatFoto ? "Neues Foto" : "Foto hochladen"}
      </Knopf>
    </>
  );
}
